import { ThunkDispatch } from "core-integration/src/store";
import {
  autoComplete,
  fetchAutopick,
} from "core-integration/src/store/squad/thunks";
import { getTeams } from "core-integration/src/store/teams/reducers";
import { Button } from "plos/src/components/buttons/Button";
import { Sheet } from "plos/src/components/Sheet";
import { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useOnboardingContext } from "../../../../contexts/OnboardingContext";
import { useTrackingContext } from "../../../../contexts/TrackingContext";
import {
  onboarding,
  onboardingHeading,
  subtitleStyles,
} from "../onboarding.css";
import { TeamBuilderPreferencesFormData } from "../types";
import { getAutopickPreferences, getFavouriteTeamSubtitle } from "../utils";
import { ConfirmSkipDialog } from "./ConfirmSkipDialog";
import {
  BudgetAllocationForm,
  FavouriteTeamForm,
  PositionFocusForm,
  TeamBuilderSummary,
} from "./FormSteps";
import { TeamBuilderProgressBar } from "./TeamBuilderProgressBar";

interface TeamBuilderWizardProps {
  favouriteTeam: number | null;
}

// The wizard fields, in order. Each step validates its own field before
// advancing.
const wizardFields = [
  "preferFavouriteTeam",
  "budgetAllocation",
  "positionFocus",
] as const;

const TeamBuilderWizard = ({ favouriteTeam }: TeamBuilderWizardProps) => {
  const { isOpen, handleClose, setAutopickChoice, setLastAutopickPreferences } =
    useOnboardingContext();
  const { logBranchEvent } = useTrackingContext();
  const { t } = useTranslation("onboarding");
  const dispatch = useDispatch<ThunkDispatch>();
  const teams = useSelector(getTeams);

  const methods = useForm<TeamBuilderPreferencesFormData>({
    mode: "onChange",
    defaultValues: {
      budgetAllocation: "balanced_spend",
      positionFocus: "balanced_squad",
      preferFavouriteTeam: favouriteTeam ? "yes" : undefined,
    },
  });

  const [stepIndex, setStepIndex] = useState(0);
  const [showConfirmSkip, setShowConfirmSkip] = useState(false);

  // The summary is the final step, after the preference steps.
  const isSummaryStep = stepIndex === wizardFields.length;
  const teamName = teams.find(
    (team) => team.id === Number(favouriteTeam),
  )?.name;

  const stepTitles = [
    t("preferFavouriteTeamTitle", "Your Favourite Club"),
    t("budgetAllocationTitle", "Budget Allocation"),
    t("positionFocusTitle", "Position Focus"),
    t("submitTitle", "Summary"),
  ];
  const stepSubtitles = [
    getFavouriteTeamSubtitle(t, teamName),
    t(
      "budgetAllocationSubtitle",
      "You have a budget of £100m for a squad of 15 players, with 11 starting each Gameweek...",
    ),
    t(
      "positionFocusSubtitle",
      "Your squad scores points in defence and attack. Do you want to focus your budget, or keep it balanced?",
    ),
  ];

  // Populate the proposed squad via the backend autopick, falling back to the
  // local autocomplete. The user then reviews and submits from the squad page's
  // Enter Squad button.
  const completeWizard = async () => {
    const prefs = methods.getValues();
    const resolvedPrefs = getAutopickPreferences(prefs, favouriteTeam);
    // Persist so "Auto Pick Again" on the squad page can reuse the same prefs.
    setLastAutopickPreferences(resolvedPrefs);
    const result = await dispatch(fetchAutopick(resolvedPrefs));
    if (!result.success) {
      dispatch(autoComplete());
    }
    logBranchEvent("COMPLETE_TUTORIAL");
    handleClose();
  };

  const handleNext = async () => {
    if (isSummaryStep) {
      await completeWizard();
      return;
    }

    // Wait for validation to resolve before advancing
    const isStepValid = await methods.trigger(wizardFields[stepIndex]);
    if (!isStepValid) return;

    setStepIndex((prev) => prev + 1);
  };

  const handleBack = () => setStepIndex((prev) => Math.max(0, prev - 1));

  const handleSkip = () => {
    setShowConfirmSkip(false);
    setAutopickChoice("manual_pick");
    handleClose();
  };

  const renderStep = () => {
    switch (stepIndex) {
      case 0:
        return <FavouriteTeamForm favouriteTeam={favouriteTeam} />;
      case 1:
        return <BudgetAllocationForm />;
      case 2:
        return <PositionFocusForm />;
      case 3:
        return <TeamBuilderSummary />;
      default:
        return null;
    }
  };

  return (
    <FormProvider {...methods}>
      <Sheet
        open={isOpen}
        handleBack={stepIndex > 0 ? handleBack : undefined}
        handleClose={() => setShowConfirmSkip(true)}
        isDismissable
        footer={
          <Button fullWidth onPress={handleNext}>
            {isSummaryStep
              ? t("generateTeam", "Generate Team")
              : t("continue", "Continue")}
          </Button>
        }
      >
        <div className={onboarding}>
          {!isSummaryStep && (
            <TeamBuilderProgressBar
              currentStep={stepIndex + 1}
              totalSteps={wizardFields.length}
            />
          )}
          <h1 className={onboardingHeading}>{stepTitles[stepIndex]}</h1>
          {stepSubtitles[stepIndex] && (
            <p className={subtitleStyles}>{stepSubtitles[stepIndex]}</p>
          )}
          {renderStep()}
          <ConfirmSkipDialog
            isOpen={showConfirmSkip}
            onConfirm={handleSkip}
            onCancel={() => setShowConfirmSkip(false)}
          />
        </div>
      </Sheet>
    </FormProvider>
  );
};

export default TeamBuilderWizard;
